import { useI18n } from '../context/I18nContext';
import { format } from 'date-fns';
import { useDateLocale } from './useDateLocale';
import {
  formatDateLong,
  formatDateShort as formatDateShortUtil,
  formatCurrencyValue,
  formatNumberValue,
  getDateFormatString,
  getDateFormatStringCompact,
} from '../utils/formatUtils';

/**
 * Custom hook that provides translation utilities
 * This is a wrapper around useI18n for easier usage
 */
export const useTranslations = () => {
  const { language, setLanguage, t } = useI18n();
  const dateLocale = useDateLocale();

  return {
    language,
    setLanguage,
    t,
    // Helper function to format currency based on language
    formatCurrency: (value: number, currency: string = 'EUR', options?: Intl.NumberFormatOptions) => {
      return formatCurrencyValue(value, language, currency, options);
    },
    // Helper function to format dates based on language (long format)
    formatDate: (date: Date | string) => {
      return formatDateLong(date, language);
    },
    // Helper function to format dates in short format (dd/MM/yyyy or MM/dd/yyyy)
    // Uses date-fns for consistency with existing code that uses locale objects
    formatDateShort: (date: Date | string) => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const formatString = getDateFormatString(language);
      return format(dateObj, formatString, { locale: dateLocale });
    },
    /** Card-friendly short date with 2-digit year (dd/MM/yy) */
    formatDateCompact: (date: Date | string) => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const formatString = getDateFormatStringCompact(language);
      return format(dateObj, formatString, { locale: dateLocale });
    },
    // Helper function to format dates using toLocaleDateString (for consistency)
    formatDateLocale: (date: Date | string) => {
      return formatDateShortUtil(date, language);
    },
    // Helper function to format numbers based on language
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) => {
      return formatNumberValue(value, language, options);
    },
  };
};


















